import { useCallback, useEffect, useRef, useState } from 'react';
import {
  accrochageApi,
  lireRevisionPerimee,
  type PlanPresente,
  type RevisionPerimee,
} from '../../api/accrochage.js';
import { planAccrochageMiseAJourSchema } from '@artosera/shared';
import { dansLaPorteeCourante } from '../../api/cles.js';

export type ContenuPlan = Pick<PlanPresente, 'nom' | 'axeCm' | 'etat' | 'placements'>;
export function useEnregistrementAuto(
  galerie: string,
  id: string,
  initial: PlanPresente,
  contenu: ContenuPlan,
  autorise: boolean,
) {
  const courant = useRef(contenu);
  useEffect(() => {
    courant.current = contenu;
  }, [contenu]);
  const revision = useRef(initial.revision);
  const sauve = useRef(JSON.stringify(contenu));
  const enVol = useRef<Promise<number> | null>(null);
  const conflitRef = useRef<RevisionPerimee | null>(null);
  const [conflit, setConflit] = useState<RevisionPerimee | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState(false);
  const [empreinteSauvee, setEmpreinteSauvee] = useState(JSON.stringify(contenu));
  const enregistrer = useCallback(
    async function enregistrerCourant(): Promise<number> {
      if (enVol.current) {
        await enVol.current;
        return enregistrerCourant();
      }
      if (conflitRef.current) throw new Error('Le plan a été modifié par un autre éditeur.');
      const empreinte = JSON.stringify(courant.current);
      if (!autorise || empreinte === sauve.current) return revision.current;
      setEnCours(true);
      setErreur(null);
      const promesse = dansLaPorteeCourante((appel) =>
        accrochageApi.enregistrerPlan(
          galerie,
          id,
          planAccrochageMiseAJourSchema.parse({ ...courant.current, revision: revision.current }),
          appel,
        ),
      )
        .then(({ plan }) => {
          revision.current = plan.revision;
          sauve.current = empreinte;
          setEmpreinteSauvee(empreinte);
          return plan.revision;
        })
        .catch((e: unknown) => {
          const perimee = lireRevisionPerimee(e);
          conflitRef.current = perimee;
          setConflit(perimee);
          setErreur(e);
          throw e;
        })
        .finally(() => {
          enVol.current = null;
          setEnCours(false);
        });
      enVol.current = promesse;
      return promesse;
    },
    [autorise, galerie, id],
  );
  const modifie = JSON.stringify(contenu) !== empreinteSauvee;
  useEffect(() => {
    if (!autorise || !modifie || conflit || erreur || enCours) return;
    const minuterie = setTimeout(() => {
      void enregistrer().catch(() => {
        /* L’erreur reste visible jusqu’au geste de l’éditeur. */
      });
    }, 1500);
    return () => clearTimeout(minuterie);
  }, [autorise, contenu, modifie, conflit, erreur, enCours, enregistrer]);
  return { conflit, erreur, enCours, modifie, enregistrer };
}
